import type { VoidFunctionComponent } from 'react';
import { BUTTON_SIZE, BUTTON_TYPE } from '../../../generic';
import Button from '../../../generic/Button';
import Flex from '../../../generic/Flex';
import { useGetCurrentPlan } from '../../../../hooks/subscriptions/useGetCurrentPlan';
import { useRouter } from '../../../../contexts/RouterContext';
import { getSubscriptionPlanName } from '../utils';
import { isOrgInFreeTier } from '../../../../helpers/subscriptions';
import styles from '../style.module.less';
import Loader from '../../../generic/Loader';

/**
 * This component displays the current subscription plan's name and conditionally renders a call-to-action (CTA) button
 * if a CTA label is determined based on the organization's current plan status. It fetches subscription data, handles
 * loading states, and dynamically determines the CTA label.
 *
 * @returns {JSX.Element | null} - Returns the subscription label with an optional CTA button if data is available,
 * a loading indicator if the data is being fetched, or null if no data is available.
 *
 * @example
 * <SubscriptionLabel />
 */
export const SubscriptionLabel: VoidFunctionComponent = () => {
  const {
    data,
    isLoading,
  } = useGetCurrentPlan();
  const router = useRouter();

  if (isLoading) {
    return (<Loader size={20} />);
  }

  if (!data) {
    return null;
  }

  let ctaLabel = '';
  if (!isOrgInFreeTier() && data.trialDaysRemaining) {
    ctaLabel = 'Trial in Progress';
  } else if (isOrgInFreeTier()) {
    ctaLabel = data.hasUsedFreeTrial ? 'Upgrade Now!' : 'Start Trial';
  }

  return (
    <Flex justify="space-between" gap={60}>
      <div className={styles.subscriptionLabelText}>{getSubscriptionPlanName(data)}</div>
      {ctaLabel &&
        <Button
          type={BUTTON_TYPE.PRIMARY}
          size={BUTTON_SIZE.MICRO}
          onClick={() => {
            router.push('/account/settings/billing');
          }}
        >{ctaLabel}
        </Button>}
    </Flex>
  );
};
